import React, { ReactNode, useId, useRef, useState } from 'react';
import { apm } from '@elastic/apm-rum';
import { Flex } from 'glints-aries/es';
import {
  Icon,
  InlineError,
  OutlineButton,
  Typography,
} from 'glints-aries/es/@next';
import { Neutral, Orange } from 'glints-aries/es/@next/utilities/colors';
import {
  space4,
  space8,
  space12,
} from 'glints-aries/es/@next/utilities/spacing';
import { FieldPath, useController, useWatch } from 'react-hook-form';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import styled from 'styled-components';

import { ASSET_TYPES, AssetType } from '../../../../common/constants';
import {
  countryCodeIsIndonesia,
  countryCodeIsSingapore,
} from '../../../../common/helpers/countryCodeHelper';
import useUploadFile from '../../../../common/hooks/requests/useUploadFile';
import { CompanyNPWPDocumentMetadata } from '../../../../common/interfaces/entities';
import { CTAText, StyledLink } from '../../../CustomSpan';
import {
  VideoTabs,
  VideoTutorialModalLinks,
} from '../../DocumentTutorialModal/constants';
import { LegalDocumentMetadataChecks, LegalDocumentSource } from '../constants';
import {
  useCompanyVerificationFormContext,
  useIsViewOnlyMode,
} from '../FormContext';
import { extractSingaporeUenFromAcraBizFile } from '../helpers/acraBizFileUen';
import { useCompanyVerificationTracking } from '../hooks/useCompanyVerificationTracking';
import { CompanyVerificationFormState } from '../types';
import { getAssetFileNameFromURL, isURL } from '../utils';
import FileItem from './FileItem';
import { FormContentStyles } from './styles.sc';

type ValidateImageQualityResult = {
  isValid: boolean;
  error?: {
    code: string;
    minScore?: number;
    score?: number;
  };
};

type Props = {
  title: ReactNode;
  subtitle?: ReactNode;
  hideTitle?: boolean;
  buttonText: ReactNode;
  required?: boolean;
  assetType: AssetType;
  fieldName: FieldPath<CompanyVerificationFormState>;
  tooltip?: ReactNode;
  helperText: MessageDescriptor;
  maxFileSizeInMB?: number;
  acceptedFileType?: string[];
  openTutorialModal?: () => void;
  disabled?: boolean;
  companyCountryCode?: string;
  validateImageQuality?: (
    uploadedFilePath: string
  ) => Promise<ValidateImageQualityResult>;
};

const StyledWarning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${space8};
`;

const InlineWarning = ({
  openTutorialModal,
}: {
  openTutorialModal: () => void;
}) => (
  <StyledWarning>
    <Icon
      name="ri-error-warning-fill"
      height={20}
      width={20}
      fill={Orange.S87}
    />
    <Typography
      variant="subtitle2"
      color={Orange.S87}
      as={'span'}
      style={{
        width: 'fit-content',
      }}
    >
      <FormattedMessage
        id="text-this-pdf-may-not-be"
        defaultMessage="Our system detected that the PDF is not from an official source, which could delay verification. Please download your NPWP from <djpLink>DJP Online</djpLink> or <coretaxLink>Coretax</coretaxLink> website, or <tutorial>follow the tutorial</tutorial>"
        values={{
          djpLink: (...chunks: ReactNode[]) => (
            <StyledLink
              link={VideoTutorialModalLinks[VideoTabs.DJP_WEBSITE].websiteLink}
            >
              {chunks}
            </StyledLink>
          ),
          coretaxLink: (...chunks: ReactNode[]) => (
            <StyledLink
              link={
                VideoTutorialModalLinks[VideoTabs.CORETAX_WEBSITE].websiteLink
              }
            >
              {chunks}
            </StyledLink>
          ),
          tutorial: (...chunks: ReactNode[]) => (
            <CTAText onClick={openTutorialModal}>{chunks}</CTAText>
          ),
        }}
      />
    </Typography>
  </StyledWarning>
);

const DEFAULT_MAX_FILE_SIZE = 10;
const DEFAULT_ACCEPTED_FILE_TYPE = [
  'application/pdf',
  'image/jpeg',
  'image/png',
];
export default function FileUploader({
  title,
  subtitle,
  buttonText,
  assetType,
  required,
  fieldName,
  tooltip,
  hideTitle,
  helperText,
  maxFileSizeInMB = DEFAULT_MAX_FILE_SIZE,
  acceptedFileType = DEFAULT_ACCEPTED_FILE_TYPE,
  openTutorialModal,
  disabled,
  companyCountryCode,
  validateImageQuality,
}: Props) {
  const { control, setValue } = useCompanyVerificationFormContext();
  const {
    field: { onChange, value: fieldValue },
    fieldState: { error: fieldError },
  } = useController({
    name: fieldName,
    control,
  });
  const legalDocumentNotOfficial = useWatch({
    control,
    name: 'verifyWithLegalDocuments.legalDocumentNotOfficial',
  });
  const inputElemId = useId();
  const {
    trackCompanyVerificationFileUploadEvent,
    trackCompanyVerificationValidateImageEvent,
  } = useCompanyVerificationTracking();

  const uploadedFilePathRef = useRef<string | null>(null);

  const {
    uploadFile,
    file,
    loading,
    originalFile,
    error,
    reset: resetFile,
  } = useUploadFile({
    assetType,
    maxSizeInMB: maxFileSizeInMB,
    allowedMimeTypes: acceptedFileType,
    onSuccess: uploadedFilePath => {
      uploadedFilePathRef.current = uploadedFilePath;
    },
  });
  const isViewOnly = useIsViewOnlyMode();

  const { formatMessage } = useIntl();
  const [isImageValidating, setIsImageValidating] = useState(false);
  const [imageValidationError, setImageValidationError] = useState<
    string | null
  >(null);

  const validateUploadedImageQuality = async () => {
    const uploadedFilePath = uploadedFilePathRef.current;
    if (!uploadedFilePath) return false;

    if (!validateImageQuality) {
      return true;
    }

    setIsImageValidating(true);

    try {
      const result = await validateImageQuality(uploadedFilePath);
      trackCompanyVerificationValidateImageEvent(result);

      if (!result.isValid) {
        setImageValidationError(
          formatMessage({
            id: 'text-uploaded-document-blurry',
            defaultMessage:
              "Your uploaded document is blurry or black and white, so the system couldn't verify it. Please upload a clear, color version of the original NPWP document.",
          })
        );

        return false;
      }
    } catch (error) {
      // validateImageQuality is not a critical path, should not impact the main logic
      apm.captureError(error as Error);
    } finally {
      setIsImageValidating(false);
    }

    return true;
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    uploadedFilePathRef.current = null;
    setImageValidationError(null);

    // We upload the file regardless if it pass the metadata check or not
    await uploadFile(file);

    if (!(await validateUploadedImageQuality())) {
      return;
    }

    onChange(uploadedFilePathRef.current);
    trackCompanyVerificationFileUploadEvent(fieldName);

    if (
      countryCodeIsIndonesia(companyCountryCode) &&
      assetType === ASSET_TYPES.COMPANY_LEGAL_DOCUMENT &&
      file.type === 'application/pdf'
    ) {
      const { PDFDocument } = await import('pdf-lib');
      const arrayBuffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer, {
        updateMetadata: false,
      });

      const metadata = {
        producer: pdfDoc.getProducer() || null,
        creator: (pdfDoc.getCreator() ?? pdfDoc.getAuthor()) || null,
        createdAt: pdfDoc.getCreationDate() || null,
        modifiedAt: pdfDoc.getModificationDate() || null,
      };

      // Check for Indonesia timezone offset
      const ID_TIMEZONE_OFFSET_MINUTE = -420;
      if (
        !metadata.createdAt ||
        metadata.createdAt.getTimezoneOffset() !== ID_TIMEZONE_OFFSET_MINUTE
      ) {
        setValue('verifyWithLegalDocuments.legalDocumentNotOfficial', true);
      }

      let documentSource: LegalDocumentSource | null = null;

      for (const [source, { creator, producer }] of Object.entries(
        LegalDocumentMetadataChecks
      )) {
        const validCreator = metadata.creator?.includes(creator);
        const validProducer = metadata.producer?.includes(producer);

        if (validCreator && validProducer) {
          documentSource = source as LegalDocumentSource;
          break;
        }
      }

      if (documentSource) {
        const metadataPayload = {
          ...metadata,
          source: documentSource,
        } as CompanyNPWPDocumentMetadata;
        setValue(
          'verifyWithLegalDocuments.companyNPWPDocMetadata',
          metadataPayload,
          {
            shouldValidate: true,
          }
        );
      } else {
        setValue('verifyWithLegalDocuments.legalDocumentNotOfficial', true);
        setValue('verifyWithLegalDocuments.companyNPWPDocMetadata', metadata, {
          shouldValidate: true,
        });
      }
    } else if (
      countryCodeIsIndonesia(companyCountryCode) &&
      assetType === ASSET_TYPES.COMPANY_LEGAL_DOCUMENT
    ) {
      setValue('verifyWithLegalDocuments.legalDocumentNotOfficial', true);
    }

    if (
      countryCodeIsSingapore(companyCountryCode) &&
      assetType === ASSET_TYPES.ACRA_BIZFILE &&
      file.type === 'application/pdf'
    ) {
      const uen = await extractSingaporeUenFromAcraBizFile(file);

      if (uen) {
        setValue('verifyWithLegalDocuments.companyUniqueEntityNumber', uen, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
  };

  const handleRemoveFile = () => {
    resetFile();
    onChange('');
    setImageValidationError(null);
    setValue('verifyWithLegalDocuments.legalDocumentNotOfficial', false);
  };

  const selectedFile = fieldValue
    ? isURL(fieldValue as string)
      ? {
          assetURL: fieldValue as string,
          assetFileName: getAssetFileNameFromURL(fieldValue as string),
        }
      : {
          assetURL:
            (file && originalFile ? URL.createObjectURL(originalFile) : '') ||
            (fieldValue as string),
          assetFileName: file?.name || (fieldValue as string),
        }
    : file && originalFile
      ? {
          assetURL: URL.createObjectURL(originalFile),
          assetFileName: file.name,
        }
      : null;

  const handleReupload = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleRemoveFile();
    handleFileChange(e);
  };

  const visibleErrorMessage =
    imageValidationError ??
    (!loading && !selectedFile ? fieldError?.message : undefined);

  return (
    <Flex flexDirection="column" gap={space12} alignItems="flex-start">
      <If condition={!hideTitle}>
        <Flex flexDirection="column" gap={space4}>
          <Flex gap={space4}>
            <Typography variant="body2">
              {title}
              {required && <FormContentStyles.Asterisk />}
            </Typography>
            {tooltip}
          </Flex>
          {subtitle}
        </Flex>
      </If>
      <Choose>
        <When condition={selectedFile}>
          {selectedFile && (
            <>
              <FileItem
                file={selectedFile}
                handleRemoveFile={() => {
                  if (isViewOnly || disabled || isImageValidating) return;
                  handleRemoveFile();
                }}
                removeFileDisabled={disabled || isImageValidating}
                showReupload={Boolean(imageValidationError)}
                acceptedFileType={acceptedFileType}
                handleReupload={handleReupload}
              />
              {legalDocumentNotOfficial && openTutorialModal && (
                <InlineWarning openTutorialModal={openTutorialModal} />
              )}
            </>
          )}
        </When>
        <Otherwise>
          <Flex flexDirection="column" gap={space8}>
            <input
              type="file"
              id={inputElemId}
              accept={acceptedFileType.join(', ')}
              hidden={true}
              onChange={handleFileChange}
            />
            <label htmlFor={inputElemId}>
              <OutlineButton
                icon={<Icon name="ri-upload-fill" />}
                style={{
                  maxWidth: 'max-content',
                }}
                loading={loading}
                onClick={e => e.currentTarget.parentElement?.click()}
                disabled={disabled}
              >
                <Typography variant="button">{buttonText}</Typography>
              </OutlineButton>
            </label>
            {error && <InlineError text={error} />}
            {legalDocumentNotOfficial && openTutorialModal && (
              <InlineWarning openTutorialModal={openTutorialModal} />
            )}
            <Typography variant="subtitle2" color={Neutral.B40}>
              <FormattedMessage
                {...helperText}
                values={{
                  bold: (...chunks: ReactNode[]) => (
                    <Typography variant="caption" as="span" color={Neutral.B40}>
                      {chunks}
                    </Typography>
                  ),
                  djpLink: (...chunks: ReactNode[]) => (
                    <StyledLink
                      link={
                        VideoTutorialModalLinks[VideoTabs.DJP_WEBSITE]
                          .websiteLink
                      }
                    >
                      {chunks}
                    </StyledLink>
                  ),
                  coretaxLink: (...chunks: ReactNode[]) => (
                    <StyledLink
                      link={
                        VideoTutorialModalLinks[VideoTabs.CORETAX_WEBSITE]
                          .websiteLink
                      }
                    >
                      {chunks}
                    </StyledLink>
                  ),
                  maxSize: maxFileSizeInMB,
                }}
              />
            </Typography>
          </Flex>
        </Otherwise>
      </Choose>
      {visibleErrorMessage && <InlineError text={visibleErrorMessage} />}
    </Flex>
  );
}
